import { useState, useMemo } from 'react';
import { useExpenses } from '../contexts/ExpenseContext';
import { CATEGORIES, MONTHS, ExpenseCategory } from '../types';
import { PDFDocument } from 'pdf-lib';
import { 
  Download,
  FileText,
  Loader2,
  CheckCircle,
  Package,
  Calendar,
  AlertCircle
} from 'lucide-react';

export default function Export() {
  const { expenses } = useExpenses();
  
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedCategories, setSelectedCategories] = useState<ExpenseCategory[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState<{ name: string; url: string }[]>([]);
  const [error, setError] = useState('');

  // Get years from 2020 to current year (matching Google Drive structure)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const allYears: number[] = [];
    for (let year = currentYear; year >= 2020; year--) {
      allYears.push(year);
    }
    return allYears;
  }, []);

  // Get expenses for selected month/year
  const monthExpenses = useMemo(() => {
    return expenses.filter(e => e.month === selectedMonth && e.year === selectedYear);
  }, [expenses, selectedMonth, selectedYear]);

  // Group by category
  const expensesByCategory = useMemo(() => {
    const grouped: Record<ExpenseCategory, typeof expenses> = {} as Record<ExpenseCategory, typeof expenses>;
    CATEGORIES.forEach(cat => {
      grouped[cat.value] = monthExpenses.filter(e => e.category === cat.value);
    });
    return grouped;
  }, [monthExpenses]);

  const toggleCategory = (cat: ExpenseCategory) => {
    setSelectedCategories(prev => 
      prev.includes(cat) 
        ? prev.filter(c => c !== cat)
        : [...prev, cat]
    );
  };

  const selectAll = () => {
    const catsWithDocs = CATEGORIES.filter(cat => expensesByCategory[cat.value].length > 0).map(c => c.value);
    setSelectedCategories(catsWithDocs);
  };

  const clearSelection = () => {
    setSelectedCategories([]);
  };

  const dataURLtoBlob = (dataURL: string): Blob => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const generatePDFBinder = async (category: ExpenseCategory): Promise<{ name: string; blob: Blob } | null> => {
    const categoryExpenses = expensesByCategory[category];
    if (categoryExpenses.length === 0) return null;

    const pdfDoc = await PDFDocument.create();
    const categoryLabel = CATEGORIES.find(c => c.value === category)?.label || category;

    for (const expense of categoryExpenses) {
      try {
        if (expense.fileType === 'application/pdf') {
          // Embed existing PDF
          const pdfBytes = await fetch(expense.fileData).then(r => r.arrayBuffer());
          const existingPdf = await PDFDocument.load(pdfBytes);
          const pages = await pdfDoc.copyPages(existingPdf, existingPdf.getPageIndices());
          pages.forEach(page => pdfDoc.addPage(page));
        } else if (expense.fileType.startsWith('image/')) {
          // Embed image as PDF page
          const imageBlob = dataURLtoBlob(expense.fileData);
          const imageBytes = await imageBlob.arrayBuffer();
          
          let image;
          if (expense.fileType === 'image/png') {
            image = await pdfDoc.embedPng(imageBytes);
          } else {
            image = await pdfDoc.embedJpg(imageBytes);
          }

          // Create a page with the image
          const aspectRatio = image.width / image.height;
          let pageWidth = 612; // Letter size
          let pageHeight = 792;

          if (aspectRatio > 1) {
            // Landscape image
            pageHeight = pageWidth / aspectRatio;
          } else {
            // Portrait image
            pageWidth = pageHeight * aspectRatio;
          }

          const page = pdfDoc.addPage([612, 792]);
          const scale = Math.min(
            (612 - 40) / image.width,
            (792 - 40) / image.height
          );
          
          const scaledWidth = image.width * scale;
          const scaledHeight = image.height * scale;
          
          page.drawImage(image, {
            x: (612 - scaledWidth) / 2,
            y: (792 - scaledHeight) / 2,
            width: scaledWidth,
            height: scaledHeight,
          });
        }
      } catch (err) {
        console.error(`Failed to add ${expense.filename} to PDF:`, err);
      }
    }

    if (pdfDoc.getPageCount() === 0) return null;

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
    const monthName = MONTHS[selectedMonth - 1];
    const filename = `${categoryLabel.replace(/\s+/g, '_')}_${monthName}_${selectedYear}.pdf`;

    return { name: filename, blob };
  };

  const handleGenerate = async () => {
    if (selectedCategories.length === 0) {
      setError('Please select at least one category');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedFiles([]);

    try {
      const files: { name: string; url: string }[] = [];

      for (const category of selectedCategories) {
        const result = await generatePDFBinder(category);
        if (result) {
          const url = URL.createObjectURL(result.blob);
          files.push({ name: result.name, url });
        }
      }

      setGeneratedFiles(files);
    } catch (err) {
      setError('Failed to generate PDF binders. Please try again.');
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadAll = async () => {
    // Download each file
    for (const file of generatedFiles) {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name;
      link.click();
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between downloads
    }
  };

  const totalSelected = selectedCategories.reduce(
    (sum, cat) => sum + expensesByCategory[cat].length, 
    0
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Export PDF Binders</h1>
        <p className="text-white/60 mt-1">Combine receipts into PDF binders for your CPA</p>
      </div>

      {error && (
        <div className="flex items-center space-x-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Month/Year Selection */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="flex items-center space-x-3 mb-4">
          <Calendar className="w-5 h-5 text-shuttle-sand" />
          <h2 className="text-lg font-semibold text-white">Select Period</h2>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-1">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-shuttle-dark border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand transition-all [&>option]:bg-shuttle-dark [&>option]:text-white"
            >
              {MONTHS.map((month, i) => (
                <option key={i} value={i + 1} className="bg-shuttle-dark text-white">{month}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-shuttle-dark border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand transition-all [&>option]:bg-shuttle-dark [&>option]:text-white"
            >
              {years.map(year => (
                <option key={year} value={year} className="bg-shuttle-dark text-white">{year}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-sm text-white/50 mt-3">
          {monthExpenses.length} document{monthExpenses.length !== 1 ? 's' : ''} in {MONTHS[selectedMonth - 1]} {selectedYear}
        </p>
      </div>

      {/* Category Selection */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Package className="w-5 h-5 text-shuttle-sand" />
            <h2 className="text-lg font-semibold text-white">Select Categories</h2>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={selectAll}
              className="text-sm text-shuttle-sky hover:text-white transition-colors"
            >
              Select All
            </button>
            <span className="text-white/30">|</span>
            <button
              onClick={clearSelection}
              className="text-sm text-shuttle-sky hover:text-white transition-colors"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {CATEGORIES.map((cat) => {
            const count = expensesByCategory[cat.value].length;
            const isSelected = selectedCategories.includes(cat.value);
            return (
              <button
                key={cat.value}
                onClick={() => toggleCategory(cat.value)}
                disabled={count === 0}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-shuttle-sand bg-shuttle-sand/20'
                    : count === 0
                    ? 'border-white/10 opacity-50 cursor-not-allowed'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <span className="text-2xl block mb-1">{cat.icon}</span>
                <p className="text-sm font-medium text-white truncate">{cat.label}</p>
                <p className="text-xs text-white/50">{count} doc{count !== 1 ? 's' : ''}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Preview */}
      {selectedCategories.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4">📋 Binders to Generate</h3>
          <div className="space-y-2">
            {selectedCategories.map(cat => {
              const category = CATEGORIES.find(c => c.value === cat);
              const count = expensesByCategory[cat].length;
              return (
                <div key={cat} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">{category?.icon}</span>
                    <span className="text-white">{category?.label}_{MONTHS[selectedMonth - 1]}_{selectedYear}.pdf</span>
                  </div>
                  <span className="text-white/50 text-sm">{count} page{count !== 1 ? 's' : ''}</span>
                </div>
              );
            })}
          </div>
          <p className="text-sm text-white/50 mt-3">
            Total: {totalSelected} document{totalSelected !== 1 ? 's' : ''} in {selectedCategories.length} binder{selectedCategories.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={isGenerating || selectedCategories.length === 0}
        className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isGenerating ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Generating PDF Binders...</span>
          </>
        ) : (
          <>
            <FileText className="w-5 h-5" />
            <span>Generate PDF Binders</span>
          </>
        )}
      </button>

      {/* Generated Files */}
      {generatedFiles.length > 0 && (
        <div className="bg-green-500/20 backdrop-blur-md rounded-2xl p-6 border border-green-500/50">
          <div className="flex items-center space-x-3 mb-4">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Binders Ready!</h3>
          </div>
          
          <div className="space-y-2 mb-4">
            {generatedFiles.map((file, i) => (
              <a
                key={i}
                href={file.url}
                download={file.name}
                className="flex items-center justify-between p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-white" />
                  <span className="text-white">{file.name}</span>
                </div>
                <Download className="w-5 h-5 text-shuttle-sand" />
              </a>
            ))}
          </div>

          {generatedFiles.length > 1 && (
            <button
              onClick={handleDownloadAll}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/20 transition-colors"
            >
              <Package className="w-5 h-5" />
              <span>Download All</span>
            </button>
          )}

          <p className="text-xs text-white/50 text-center mt-4">
            💡 Send these to your CPA by the 8th of the month!
          </p>
        </div>
      )}
    </div>
  );
}

